import { useContext, useEffect, useState } from 'react'
import globalStyles from '../../../../Styles/ComponentStyles/global.module.scss';
import styles from '../../../../Styles/ComponentStyles/ProfileStyle/ProfileNewAnnuityRothConversion.module.scss'
import { Autocomplete, Grid, InputAdornment, TextField, Tooltip, Typography } from '@mui/material'
import GlobalNavbar from '../../../../GlobalComponent/GlobalNavbar/GlobalNavbar'
import { useNavigate, useSearchParams } from 'react-router-dom';
import GlobalTextfield from '../../../../GlobalComponent/GlobalTextfield/GlobalTextfield';
import Astrict from '../../../../GlobalComponent/Astrict/Astrict';
import IconsColor from '../../../../Utils/IconsColor';
import { UserContext, UserContextPayload } from '../../../../Context/UserDetailContext';
import agent from '../../../../service/api';
import { useSnackbar } from '../../../../GlobalComponent/GlobalSnackbar/GlobalSnackbar';
import { ProfileContext, ProfileContextPayload } from '../../../../Context/ProfileDetailsContext';
import { formatWithThousandSeparator } from '../../../../Utils/HelperFunctions';
import InfoIcon from '@mui/icons-material/Info';

const NAME_REGEX = /^[A-Za-z0-9 ]{1,30}$/;
const CONTACT_REGEX = /^\d{10}$/;
const EMAIL_REGEX = /^([a-zA-Z0-9._]+)@([a-zA-Z0-9-]+)\.([a-zA-Z]{2,})(\.[a-zA-Z]{2,})?$/;
const TAX_RATE_MIN = 12;
const TAX_RATE_MAX = 37;
const TAX_RATE_RANGE_MESSAGE = 'Enter value between 12.00% and 37.00%';

const FORMAT_FIELDS = new Set([
    'rothConversionPremium',
    'currentAccountBalance',
    'employerMatch',
    'annualSpendableIncome'
]);

const PERCENT_FIELDS = new Set([
    'rothConversionAnnualPreWithdrawalRate',
    'rothConversionAnnualPostWithdrawalRate',
    'rothConversionSingleIra',
    'rothConversionPartialIra',
    'rothConversionReceivingIra',
    'percentFees',
    'rateOfReturnDuringRetirement',
    'rateOfReturnDuringWorkingYears',
    'incomeTaxRateBeforeRetirement',
    'incomeTaxRateDuringRetirement',
    'rothConversionPremiumBonus'
]);

const isFilled = (value: any) => Boolean(String(value ?? '').trim());

const FIELD_CLEAR_RULES: Record<string, Record<string, (value: any) => boolean>> = {
    universalInput: {
        firstName: (v) => NAME_REGEX.test(String(v ?? '').trim()),
        currentAge: (v) => v >= 0 && v <= 120,
        ageAtRetirement: (v) => v >= 0 && v <= 120,
        Contact: (v) => CONTACT_REGEX.test(v),
        email: (v) => EMAIL_REGEX.test(v)
    },
    rothConversionData: {
        rothConversionIssueAge: (v) => v >= 21 && v <= 80,
        rothConversionAgeLifetimeBegin: (v) => v >= 60 && v <= 99,
        rothConversionPremium: isFilled,
        rothConversionAnnualPreWithdrawalRate: isFilled,
        rothConversionAnnualPostWithdrawalRate: isFilled,
        rothConversionPeriod: (v) => Number(v) >= 1 && Number(v) <= 10,
        rothConversionSingleIra: (v) => Number(v) >= TAX_RATE_MIN && Number(v) <= TAX_RATE_MAX,
        rothConversionPartialIra: (v) => Number(v) >= TAX_RATE_MIN && Number(v) <= TAX_RATE_MAX,
        rothConversionReceivingIra: (v) => Number(v) >= TAX_RATE_MIN && Number(v) <= TAX_RATE_MAX,
        rothConversionPortfolioWithdrawalRate: isFilled,
        rothConversionWithholding: () => true,
        rothConversionLifeExpectancyAge: () => true
    }
};

const formatSeparatorValue = (rawValue: string) => {
    const value = String(rawValue ?? '').replaceAll(',', '');
    if (!Number.isNaN(Number(value)) && value.trim() !== '') {
        return Number(value).toLocaleString();
    }
    return value;
};

const clearFieldError = (prevErrors: any, section: string, field: string, value: any) => {
    const rule = FIELD_CLEAR_RULES[section]?.[field];
    if (!rule?.(value)) {
        return prevErrors;
    }
    const updatedErrors = { ...prevErrors };
    if (updatedErrors[section]) {
        delete updatedErrors[section][field];
    }
    return updatedErrors;
};

const validateUniversalInput = (data: any, requireContactAndEmail = true) => {
    const errors: any = {};
    if (!data) {
        return { errors, isValid: false };
    }
    let isValid = true;

    if (!data.firstName?.trim()) {
        errors.firstName = 'First name is required';
        isValid = false;
    } else if (!NAME_REGEX.test(data.firstName)) {
        errors.firstName = 'Please enter a valid firstname';
        isValid = false;
    }

    if (!data.currentAge) {
        errors.currentAge = 'Current age is required';
        isValid = false;
    } else if (data.currentAge < 0 || data.currentAge > 120) {
        errors.currentAge = 'Please enter a valid age between 0 and 120';
        isValid = false;
    }

    if (!data.ageAtRetirement) {
        errors.ageAtRetirement = 'Retirement age is required';
        isValid = false;
    } else if (data.ageAtRetirement <= data.currentAge) {
        errors.ageAtRetirement = 'Retirement age must be greater than current age';
        isValid = false;
    }

    if (requireContactAndEmail) {
        if (!data.Contact?.trim()) {
            errors.Contact = 'Contact number is required';
            isValid = false;
        } else if (!CONTACT_REGEX.test(data.Contact)) {
            errors.Contact = 'Please enter a valid 10-digit contact number';
            isValid = false;
        }

        if (!data.email?.trim()) {
            errors.email = 'Email is required';
            isValid = false;
        } else if (!EMAIL_REGEX.test(data.email)) {
            errors.email = 'Please enter a valid email address';
            isValid = false;
        }
    }

    return { errors, isValid };
};

const isTaxRateInRange = (value: any) => {
    const num = Number(value);
    return num >= TAX_RATE_MIN && num <= TAX_RATE_MAX;
};

const validateIraTaxRateField = (value: any, errors: any, field: string): boolean => {
    if (!value) {
        errors[field] = 'Tax rate is required';
        return false;
    }
    if (!isTaxRateInRange(value)) {
        errors[field] = TAX_RATE_RANGE_MESSAGE;
        return false;
    }
    return true;
};

const validateRothConversionData = (data: any) => {
    if (!data) {
        return { errors: {}, isValid: false };
    }

    const errors: any = {};
    let isValid = true;

    if (!data.rothConversionIssueAge) {
        errors.rothConversionIssueAge = 'Issue age is required';
        isValid = false;
    } else if (data.rothConversionIssueAge < 21 || data.rothConversionIssueAge > 80) {
        errors.rothConversionIssueAge = 'Enter age between 21 and 80';
        isValid = false;
    }

    if (!data.rothConversionAgeLifetimeBegin) {
        errors.rothConversionAgeLifetimeBegin = 'Age is required';
        isValid = false;
    } else if (data.rothConversionAgeLifetimeBegin < 60 || data.rothConversionAgeLifetimeBegin > 99) {
        errors.rothConversionAgeLifetimeBegin = 'Enter age between 60 and 99';
        isValid = false;
    }

    const requiredFields: { field: string; message: string }[] = [
        { field: 'rothConversionPremium', message: 'Premium is required' },
        { field: 'rothConversionAnnualPreWithdrawalRate', message: 'Estimated annual interest pre withdrawals is required' },
        { field: 'rothConversionAnnualPostWithdrawalRate', message: 'Estimated annual interest post withdrawals is required' },
        { field: 'rothConversionPeriod', message: 'Conversion period is required' },
        { field: 'rothConversionPortfolioWithdrawalRate', message: 'Portfolio withdrawal rate is required' },
        { field: 'rothConversionLifeExpectancyAge', message: 'Life expectancy age is required' },
        { field: 'rothConversionWithholding', message: 'This field is required' }
    ];

    requiredFields.forEach(({ field, message }) => {
        if (!data[field]) {
            errors[field] = message;
            isValid = false;
        }
    });

    if (!validateIraTaxRateField(data.rothConversionSingleIra, errors, 'rothConversionSingleIra')) {
        isValid = false;
    }
    if (!validateIraTaxRateField(data.rothConversionPartialIra, errors, 'rothConversionPartialIra')) {
        isValid = false;
    }
    if (!validateIraTaxRateField(data.rothConversionReceivingIra, errors, 'rothConversionReceivingIra')) {
        isValid = false;
    }

    return { errors, isValid };
};

const ProfileNewAnnuityRothConversion = () => {

    const {
        userDetail,
        setFlagLoader,
    } = useContext(UserContext) as UserContextPayload

    const {
        createNewUserProfile,
        updateUserProfileAPI,
        getparticularUserProfileDetail
    } = useContext(ProfileContext) as ProfileContextPayload

    const userRole = userDetail?.user_data?.user_role_type?.toLowerCase();
    const navigate = useNavigate()
    const { showSnackbar } = useSnackbar();
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('user_id');
    const isEditMode = Boolean(userId);

    const [agentList, setAgentList] = useState<any>([])

    const [finalAnnuityRothData, setFinalAnnuityRothData] = useState<any>({
        universalInput: {
            firstName: '',
            currentAge: '',
            ageAtRetirement: '',
            Contact: '',
            email: ''
        },
        rothConversionData: {
            // rothConversionProduct: "",
            rothConversionIssueAge: "",
            rothConversionAgeLifetimeBegin: "",
            rothConversionPremium: "",
            // rothConversionAnnualInterest: "",
            rothConversionAnnualPreWithdrawalRate: "",
            rothConversionAnnualPostWithdrawalRate: "",
            rothConversionPeriod: "",
            // rothConversionLifeExpectancyAge: "",
            rothConversionSingleIra: "",
            rothConversionPartialIra: "",
            rothConversionReceivingIra: "",
            rothConversionPortfolioWithdrawalRate: "",
            rothConversionWithholding: "",
            rothConversionPremiumBonus: "",
            rothConversionLifeExpectancyAge: ""
        },
        IUL: {
            annualSpendableIncome: ''
        },
        AssignProfile: {
            agentId: null,
            agentName: ''
        }
    })

    const [formErrors, setFormErrors] = useState<any>({});
    const [flagSampleProfile, setFlagSampleProfile] = useState(false);
    const [flagSaveAsSampleProfile, setFlagSaveAsSampleProfile] = useState(false);

    const handleCancel = () => {
        navigate(-1)
    }

    const handleInputChange = (section: any, field: string, value: any) => {
        let nextValue = value;

        if (FORMAT_FIELDS.has(field)) {
            nextValue = formatSeparatorValue(nextValue);
        }
        if (PERCENT_FIELDS.has(field) && (Number(nextValue) < 0 || Number(nextValue) > 100)) {
            return;
        }

        setFinalAnnuityRothData((prev: any) => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: nextValue
            }
        }));

        setFormErrors((prevErrors: any) => clearFieldError(prevErrors, section, field, nextValue));
    }

    const handleAgentChange = (event: React.SyntheticEvent, newValue: any) => {
        handleInputChange("AssignProfile", "agentId", newValue?.value);
        handleInputChange("AssignProfile", "agentName", newValue?.label);
    };

    const handleWithholdingChange = (event: React.SyntheticEvent, newValue: any) => {
        handleInputChange("rothConversionData", "rothConversionWithholding", newValue);
    }

    const transformedDataForCreate = (finalAnnuityRothData: any, flagSaveAsSample: boolean) => {
        const transformedData = {
            user_profile_type_id: 2,

            // universal input
            user_name: finalAnnuityRothData?.universalInput?.firstName,
            user_current_age: finalAnnuityRothData?.universalInput?.currentAge,
            user_retirement_age: finalAnnuityRothData?.universalInput?.ageAtRetirement,
            user_contact: finalAnnuityRothData?.universalInput?.Contact,
            user_email: finalAnnuityRothData?.universalInput?.email,

            // Roth Conversion data
            // user_roth_conversion_product: finalAnnuityRothData?.rothConversionData?.rothConversionProduct,
            user_roth_conversion_issue_age: finalAnnuityRothData?.rothConversionData?.rothConversionIssueAge,
            user_roth_conversion_age_lifetime_begin: finalAnnuityRothData?.rothConversionData?.rothConversionAgeLifetimeBegin,
            user_roth_conversion_premium: finalAnnuityRothData?.rothConversionData?.rothConversionPremium,
            // user_roth_conversion_annual_interest: finalAnnuityRothData?.rothConversionData?.rothConversionAnnualInterest,
            user_roth_conversion_pre_withdrawal_rate: finalAnnuityRothData?.rothConversionData?.rothConversionAnnualPreWithdrawalRate,
            user_roth_conversion_post_withdrawal_rate: finalAnnuityRothData?.rothConversionData?.rothConversionAnnualPostWithdrawalRate,
            user_roth_conversion_period: finalAnnuityRothData?.rothConversionData?.rothConversionPeriod,
            user_roth_conversion_life_expectancy_age: finalAnnuityRothData?.rothConversionData?.rothConversionLifeExpectancyAge,
            user_roth_conversion_single_ira: finalAnnuityRothData?.rothConversionData?.rothConversionSingleIra,
            user_roth_conversion_partial_ira: finalAnnuityRothData?.rothConversionData?.rothConversionPartialIra,
            user_roth_conversion_receiving_ira: finalAnnuityRothData?.rothConversionData?.rothConversionReceivingIra,
            user_roth_conversion_withdrawal_rate: finalAnnuityRothData?.rothConversionData?.rothConversionPortfolioWithdrawalRate,
            user_roth_conversion_tax_withholding: finalAnnuityRothData?.rothConversionData?.rothConversionWithholding,
            user_roth_conversion_premium_bonus: finalAnnuityRothData?.rothConversionData?.rothConversionPremiumBonus,

            user_tpi_contribution_data: finalAnnuityRothData?.typicalPlan?.user_tpi_contribution_data?.map((item: any) => ({
                total_contribution: item?.annualContribution,  // Adjust to API format
                duration: item?.duration
            })),

            // assigned agent
            user_iul_assigned_agent_id: finalAnnuityRothData?.AssignProfile?.agentId ? [finalAnnuityRothData?.AssignProfile?.agentId] : [],

            sample_profile_flag: !flagSaveAsSample && Boolean(finalAnnuityRothData?.sample_profile),
        }
        return transformedData
    }

    const buildApiPayload = (isUpdate: boolean) => {
        const payload: any = transformedDataForCreate(finalAnnuityRothData, flagSaveAsSampleProfile);

        if (isUpdate) {
            payload.user_profile_id = userId;
        }

        if (finalAnnuityRothData?.sample_profile && !flagSaveAsSampleProfile) {
            payload.user_email = '';
            payload.user_contact = '';
            payload.user_iul_assigned_agent_id = [];
        }

        return payload;
    };

    const validateProfileForm = (profileData: any, flagSaveAsSample: boolean): { isValid: boolean; errors: any } => {
        const requireContactAndEmail = profileData?.sample_profile ? flagSaveAsSample : true;
        const universal = validateUniversalInput(profileData?.universalInput, requireContactAndEmail);
        const roth = validateRothConversionData(profileData?.rothConversionData);

        const errors = {
            universalInput: universal.errors,
            rothConversionData: roth.errors
        };
        const isValid = universal.isValid && roth.isValid;

        return { isValid, errors };
    };

    const handleProfileFormSubmit = async () => {
        try {
            const { isValid, errors } = validateProfileForm(finalAnnuityRothData, flagSaveAsSampleProfile);

            if (!isValid) {
                setFormErrors(errors);
                showSnackbar("Please fill in all required fields correctly", "error");
                return;
            }

            setFlagLoader(true);

            const isUpdate = isEditMode && !flagSaveAsSampleProfile;
            const apiPayload = buildApiPayload(isUpdate);

            const response = isUpdate
                ? await updateUserProfileAPI(apiPayload)
                : await createNewUserProfile(apiPayload);

            if (response?.status) {
                const profileId = isUpdate ? userId : response?.data?.[0]?.user_profile_id;
                navigate(`/profile/annuityProfileDetails/annuityRoth?user_id=${profileId}`);
                showSnackbar(`Profile ${isUpdate ? 'Updated' : 'Created'} Successfully!`, "success");
            } else {
                showSnackbar(response?.error?.message || "Error creating profile", "error");
            }
        } catch (error) {
            console.log(error, "Error submitting data!");
        } finally {
            setFlagLoader(false);
            setFlagSaveAsSampleProfile(false);
        }
    }

    useEffect(() => {
        try {
            setFlagLoader(true)
            const getAgentListData = async () => {
                const response = await agent?.getAgentDetailsAPI?.get()
                if (response?.status) {
                    const agents = response?.data?.[0]?.agents;

                    if (!agents) {
                        setAgentList([]);
                        setFlagLoader(false);
                        return;
                    }

                    const filteredAgents = agents
                        .filter((agent: any) => agent?.user_status_name?.toLowerCase() === "active" && agent?.user_role_type?.toLowerCase() === "agent")
                        .map((agent: any) => ({
                            label: `${agent.user_firstname} ${agent.user_lastname}`,
                            value: agent.user_id
                        }));
                    setAgentList(filteredAgents);
                    setFlagLoader(false);
                } else {
                    showSnackbar(response?.error?.message || "Something went wrong!", "error")
                    setFlagLoader(false)
                }
            }
            if (userRole == "admin") {
                getAgentListData();
            } else {
                return
            }
        } catch (error) {
            console.log(error, "Error fetching data")
            setFlagLoader(false)
        } finally {
            setFlagLoader(false)
        }
    }, [])

    useEffect(() => {
        if (isEditMode) {
            const fetchParticularUserProfileDetail = async () => {
                try {
                    setFlagLoader(true);
                    const response = await getparticularUserProfileDetail(userId);

                    if (response?.status && response?.inputData?.[0]) {
                        const userData = response.inputData[0];

                        if (userData?.sample_profile) {
                            setFlagSampleProfile(true);
                        }

                        const emailValue = userData?.sample_profile ? '' : userData?.universalInput?.email;
                        const contactValue = userData?.sample_profile ? '' : userData?.universalInput?.Contact;

                        setFinalAnnuityRothData({
                            ...userData,
                            universalInput: {
                                ...userData?.universalInput,
                                email: emailValue,
                                Contact: contactValue
                            },
                            rothConversionData: {
                                ...userData?.rothConversionData,
                                rothConversionPremium: formatWithThousandSeparator(userData?.rothConversionData?.rothConversionPremium),
                            }
                        });
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                } finally {
                    setFlagLoader(false);
                }
            };

            fetchParticularUserProfileDetail();
        }
    }, [userId, isEditMode]);

    useEffect(() => {
        if (flagSaveAsSampleProfile) {
            handleProfileFormSubmit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [flagSaveAsSampleProfile]);

    return (
        <Grid container>
            <Grid item xs={12}>
                <GlobalNavbar
                    navbarBtnText={''}
                    navbarText={isEditMode ? "Edit Profile" : "Create New Roth Profile"}
                    flagSaveCancelBtn
                    flagHelperTooltip
                    primaryBtnClick={handleProfileFormSubmit}
                    secondaryBtnClick={handleCancel}
                    saveAsSampleProfileClick={() => { setFlagSaveAsSampleProfile(true) }}
                    iseEditMode={isEditMode}
                    flagSampleProfile={flagSampleProfile}
                    flagBorderBottom={true}
                />
            </Grid>

            <Grid item xs={12} className={`${styles.newAnnuityMainItem}`}>
                <Grid container>
                    {/* UNIVERSAL INPUT */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Typography
                                    component={'span'}
                                    className={`${globalStyles.globalPFlabel}`}
                                >
                                    Universal Inputs
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>
                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Name <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Name'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'firstName', e.target.value)}
                                                    value={finalAnnuityRothData?.universalInput?.firstName}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.firstName}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Age at Retirement <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age at Retirement'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleInputChange('universalInput', 'ageAtRetirement', e.target.value)}
                                                    value={finalAnnuityRothData?.universalInput?.ageAtRetirement}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.ageAtRetirement}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Current Age <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleInputChange('universalInput', 'currentAge', e.target.value)}
                                                    value={finalAnnuityRothData?.universalInput?.currentAge}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.currentAge}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Cell Phone <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder={`${finalAnnuityRothData?.sample_profile ? '' : 'Enter Cell Phone'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleInputChange('universalInput', 'Contact', e.target.value)}
                                                    value={finalAnnuityRothData?.universalInput?.Contact}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.Contact}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Email <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder={`${finalAnnuityRothData?.sample_profile ? '' : 'Enter Email'}`}
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onChange={(e) => handleInputChange('universalInput', 'email', e.target.value)}
                                                    value={finalAnnuityRothData?.universalInput?.email}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors.universalInput?.email}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>

                    {/* Annuity - Roth Conversion */}
                    <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                        <Grid container>
                            <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                <Typography
                                    component={'span'}
                                    className={`${globalStyles.globalPFlabel}`}
                                >
                                    Annuity - Roth Conversion
                                </Typography>
                            </Grid>

                            <Grid item xs={12}>
                                <Grid container className={`${globalStyles.globalPFFieldContainer}`}>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Issue Age <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Issue Age'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionIssueAge', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionIssueAge}
                                                />
                                                <label className={formErrors.rothConversionData?.rothConversionIssueAge ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Enter age between 21 and 80"}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Age when lifetime withdrawals begins <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionAgeLifetimeBegin', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionAgeLifetimeBegin}
                                                />
                                                <label className={formErrors.rothConversionData?.rothConversionAgeLifetimeBegin ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Enter an age between 60 and 99"}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Premium <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="dollarIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionPremium', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionPremium}
                                                />
                                                {/* <label className={formErrors.rothConversionData?.rothConversionPremium ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Enter dollar amount between $20,000 and $1,000,000"}</label> */}
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.rothConversionData?.rothConversionPremium}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Premium Bonus (%)
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        const value = e.target.value;
                                                        if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionPremiumBonus', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionPremiumBonus}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Annual withdrawal rate (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        const value = e.target.value;
                                                        if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionPortfolioWithdrawalRate', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionPortfolioWithdrawalRate}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.rothConversionData?.rothConversionPortfolioWithdrawalRate}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Conversion Period (years) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Years'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionPeriod', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionPeriod}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.rothConversionData?.rothConversionPeriod}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Life expectancy (age) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder='Enter Age'
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        if (!/\d$/.test(e.key)) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionLifeExpectancyAge', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionLifeExpectancyAge}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.rothConversionData?.rothConversionLifeExpectancyAge}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Grid container alignItems="center" justifyContent="space-between">
                                                    <Grid item>
                                                        <Typography
                                                            component='span'
                                                            className={`${globalStyles.globalPFTextfieldLabel}`}
                                                        >
                                                            Income tax withholding? <Astrict />
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Tooltip
                                                            title={
                                                                <>
                                                                    <p>Yes: You would not be paying out of the pocket.</p>
                                                                    <p>No: You would be paying out of the pocket.</p>
                                                                </>
                                                            }
                                                            placement="top-end"
                                                            arrow
                                                            componentsProps={{
                                                                tooltip: {
                                                                    sx: {
                                                                        backgroundColor: "#3B78CE",
                                                                        color: "white",
                                                                        border: "1px solid #3B78CE",
                                                                        boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
                                                                        fontSize: "13px",
                                                                        fontWeight: "500",
                                                                        maxWidth: "400px",
                                                                        padding: "8px 12px",
                                                                        "& .MuiTooltip-arrow": {
                                                                            color: "#3B78CE"
                                                                        }
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <InfoIcon sx={{ color: "#3B78CE" }} />
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <Autocomplete
                                                    options={['Yes', 'No']}
                                                    className={globalStyles.globalAutoCompleteStyle}
                                                    onChange={handleWithholdingChange}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionWithholding}
                                                    renderInput={(params) => <TextField placeholder='Select' {...params} />}
                                                    sx={{
                                                        "& .MuiInputBase-root": {
                                                            padding: "3px !important"
                                                        }
                                                    }}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.rothConversionData?.rothConversionWithholding}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Estimated annual interest pre withdrawals (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        const value = e.target.value;
                                                        if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionAnnualPreWithdrawalRate', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionAnnualPreWithdrawalRate}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.rothConversionData?.rothConversionAnnualPreWithdrawalRate}</label>
                                                {/* <label className={formErrors.rothConversionData?.rothConversionAnnualPreWithdrawalRate ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Interest rate on the total portfolio amount pre withdrawals"}</label> */}
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Estimated annual interest post withdrawals (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        const value = e.target.value;
                                                        if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionAnnualPostWithdrawalRate', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionAnnualPostWithdrawalRate}
                                                />
                                                <label className={globalStyles.helperTextStyle}>{formErrors?.rothConversionData?.rothConversionAnnualPostWithdrawalRate}</label>
                                                {/* <label className={formErrors.rothConversionData?.rothConversionAnnualPostWithdrawalRate ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Interest rate on the total portfolio amount post withdrawals"}</label> */}
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Tax rate for single Roth IRA conversion (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        const value = e.target.value;
                                                        if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionSingleIra', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionSingleIra}
                                                />
                                                <label className={formErrors.rothConversionData?.rothConversionSingleIra ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Enter value between 12.00% and 37.00%"}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    {/* Tax rate if series of partial Roth IRA conversions (%) <Astrict /> */}
                                                    Tax rate for partial Roth IRA conversion (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        const value = e.target.value;
                                                        if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionPartialIra', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionPartialIra}
                                                />
                                                <label className={formErrors.rothConversionData?.rothConversionPartialIra ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Enter value between 12.00% and 37.00%"}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                    <Grid item className={`${globalStyles.globalProfileFieldsWrapper}`}>
                                        <Grid container>
                                            <Grid item xs={12} className={`${globalStyles.globalPFLabel}`}>
                                                <Typography component={'span'} className={`${globalStyles.globalPFTextfieldLabel}`}>
                                                    Tax rate while receiving lifetime withdrawals (%) <Astrict />
                                                </Typography>
                                            </Grid>
                                            <Grid item xs={12}>
                                                <GlobalTextfield
                                                    variant="outlined"
                                                    placeholder=''
                                                    size="medium"
                                                    fullWidth
                                                    autoComplete='off'
                                                    stylingClassName={`${globalStyles.globalPFTextfield}`}
                                                    onKeyPress={(e: any) => {
                                                        const value = e.target.value;
                                                        if (!/^[0-9.]$/.test(e.key) || (e.key === '.' && value.includes('.'))) {
                                                            e.preventDefault();
                                                        }
                                                    }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <IconsColor iconName="percentageIcon" color="rgba(145, 145, 145, 1)" width="16px" height="16px" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    onChange={(e) => handleInputChange('rothConversionData', 'rothConversionReceivingIra', e.target.value)}
                                                    value={finalAnnuityRothData?.rothConversionData?.rothConversionReceivingIra}
                                                />
                                                <label className={formErrors.rothConversionData?.rothConversionReceivingIra ? globalStyles.helperTextStyle : styles.helperTextStyle}>{"Enter value between 12.00% and 37.00%"}</label>
                                            </Grid>
                                        </Grid>
                                    </Grid>

                                </Grid>
                            </Grid>

                        </Grid>
                    </Grid>

                    {/* ASSIGN AGENT SECTION */}
                    {userRole != "agent" &&
                        <Grid item xs={12} className={`${globalStyles.globalPFBlurWrapper}`}>
                            <Grid container>
                                <Grid item xs={12} className={`${globalStyles.globalPFSectioHeadItem}`}>
                                    <Typography
                                        component={'span'}
                                        className={`${globalStyles.globalPFlabel}`}
                                    >
                                        Assign Profile
                                    </Typography>
                                </Grid>

                                <Grid item xs={12} sm={12} md={9} lg={11} xl={4.5}>
                                    <Grid container>
                                        <Grid item className={`${globalStyles.globalPFLabel}`}>
                                            <Typography
                                                component='span'
                                                className={`${globalStyles.globalPFTextfieldLabel}`}
                                            >
                                                Agent Name
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Autocomplete
                                                options={agentList}
                                                className={globalStyles.globalAutoCompleteStyle}
                                                onChange={handleAgentChange}
                                                value={finalAnnuityRothData?.AssignProfile?.agentName}
                                                renderInput={(params) => <TextField placeholder='Select Agent' {...params} />}
                                                sx={{
                                                    "& .MuiInputBase-root": {
                                                        padding: "3px !important"
                                                    }
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    }
                </Grid>
            </Grid>
        </Grid >
    )
}


export default ProfileNewAnnuityRothConversion